import React from 'react'
import calculateVertex from '../helpers/calculateVertex'
import calculateBoundPoints from '../helpers/calculateBoundPoints'
import calculatePpf from 'helpers/calculations/calculatePpf'
import calculateHorizontalFieldOfView from 'helpers/calculations/calculateHorizontalFieldOfView'
import toRadian from 'helpers/toRadian'
import { CONVENTIONAL, ANALYTIC_ZONE_OPACITIES } from 'helpers/globals'
import zipWith from 'ramda/src/zipWith'
import calculateClippedView, { computeIntersection, calculateClippedCircle, computeRingIntersection } from '../helpers/calculateClippedView'

export default class AnalyticZones extends React.Component {
  updateView = mapFun => (Array.isArray(this.view) ? this.view : [this.view]).map(mapFun)

  componentWillMount() {
    if (this.props.showCameraComponents && this.props.showAnalyticZones && this.props.mapLoaded) this.drawView(this.props)
  }

  getPaths(paths) {
    if (paths.halfCircle) {
      return paths.halfCircle
    } else if (paths.endAngle) {
      return paths.endAngle
    } else {
      const { cameraMarkerPosition, leftPosition, rightPosition } = paths
      return [...Object.values({ cameraMarkerPosition, leftPosition, rightPosition })]
    }
  }

  drawView(args) {
    const { verticalAngleOfView, horizontalAngleOfView, map, color, cameraHeight, setCurrentIfApplicable, tilt, showCameraBlindspot,
      distance, fovMode, addToMarkers, activeInUI, selectionOverride, horizontalResolution, verticalResolution, heading,
      cameraMarkerPosition, showAnalyticZones, showWallBlindspots, walls, subjectMarkerPosition, ppfZoneBorders, ppfZoneColorOverride,
      ppfZoneColors } = args

    const verticalAngle = fovMode === CONVENTIONAL ? verticalAngleOfView : horizontalAngleOfView
    const horizontalAngle = fovMode !== CONVENTIONAL ? verticalAngleOfView : horizontalAngleOfView
    const resolution = fovMode === CONVENTIONAL ? horizontalResolution : verticalResolution

    // wipe any present analytic zones from the view
    if (this.view) this.updateView(el => el.setMap(null))

    // find angle of bounds
    const closeAngle = 90 + (tilt - verticalAngle)
    const farAngle = closeAngle + verticalAngle

    // find distance to bounds
    const closerBound = Math.tan(toRadian(closeAngle)) * cameraHeight
    const furtherBound = Math.tan(toRadian(farAngle)) * cameraHeight
    const useBlindspot = showCameraBlindspot && closerBound > 0

    let pathArr = []
    let circleArray = [] // contains circles and polygons generated for 180/270/360 deg camera configs
    let polygonArray = [] // contains polygons generated for cameras with AoV < 180 deg

    const defaults = { strokeOpacity: 0, zIndex: activeInUI || selectionOverride ? 104 : 102, map }

    const ppfRaw = zipWith((c, e) => ({ ...e, color: c }), ppfZoneColors, zipWith((o, v) => ({ fillOpacity: o, value: v }), ANALYTIC_ZONE_OPACITIES, ppfZoneBorders)) // get the opacities and list of zones
    let ppfUsable = [] // if blindspot is enabled, add only ppf zones that are past the blindspot border

    let clippedView, clippedCircle
    if (showWallBlindspots) {
      if (horizontalAngle < 180) {
        clippedView = calculateClippedView({ walls, angle: horizontalAngle, distance, heading, cameraMarkerPosition, map })
      } else {
        const outerCirclePath = calculateVertex({ angle: horizontalAngle, distance, heading, cameraMarkerPosition, showAnalyticZones: true })
        clippedCircle = calculateClippedCircle({ walls, path: outerCirclePath, distance, cameraMarkerPosition, subjectMarkerPosition, map })
      }
    }

    if (horizontalAngle < 180) {
      if (useBlindspot && closerBound < distance) { // start by generating invisible polygon from start --> blindspot
        pathArr.push({ path: calculateVertex({ angle: horizontalAngle, distance: closerBound, heading, cameraMarkerPosition, showAnalyticZones }) })
        pathArr[0].path.push(cameraMarkerPosition) // link path back to starting position
        const tempppf = calculatePpf({ horizontalResolution: resolution, horizontalFieldOfView: calculateHorizontalFieldOfView({ horizontalAngleOfView: horizontalAngle, distance: closerBound }) })
        ppfUsable.push({ value: tempppf, fillOpacity: 0.90, color: '' }) // create the first border
        let k

        for (let i = 1; i < ppfRaw.length; i++) {
          if (ppfRaw[i].value < tempppf) {
            if (!k) k = i
            ppfUsable.push(ppfRaw[i])
          }
        }

        if (!k) k = ppfRaw.length
        ppfUsable[0].fillOpacity = ppfRaw[k - 1].fillOpacity // find index of PPF zone immediately after blindspot
        ppfUsable[0].color = ppfRaw[k - 1].color
      } else if (useBlindspot) { // add nothing to map if blindspot is enabled and blindspot border is outside of marker
        return
      } else {
        ppfUsable = ppfRaw // if blindspot is disabled, use the entire list without modification
      }

      for (let i = 0; i < ppfUsable.length; i++) {
        const calculatedDistance = (resolution * 180) / (ppfUsable[i].value * horizontalAngle * Math.PI)

        if (i === 0 && calculatedDistance < distance) { // draw first analytic zone from start --> 100 PPF
          pathArr.push({ path: calculateVertex({ angle: horizontalAngle, distance: calculatedDistance, heading, cameraMarkerPosition, showAnalyticZones }) })
          pathArr[0].path.push(cameraMarkerPosition)
        } else if (calculatedDistance < distance) { // otherwise add zones as necessary, and add point on same heading as marker line so that the polygon covers the etnire area
          pathArr.push({ path: calculateVertex({ angle: horizontalAngle, distance: calculatedDistance, heading, cameraMarkerPosition, showAnalyticZones }) })
        } else break
      }

      if (pathArr.length > 0) {
        let j = 0
        for (j = 0; j + 1 < pathArr.length; j++) {
          const newPathArr = j === 0 ? pathArr[j].path : [...pathArr[j].path.reverse(), ...pathArr[j + 1].path] // combine both curved paths together and generate a polygon
          polygonArray.push(new google.maps.Polygon({
            ...defaults,
            fillColor: ppfZoneColorOverride ? (useBlindspot && ppfUsable[j - 1] ? ppfUsable[j - 1].color : ppfUsable[j].color) : color,
            fillOpacity: j === 0 && useBlindspot && closerBound < distance ? 0 : (useBlindspot && ppfUsable[j - 1] ? ppfUsable[j - 1].fillOpacity : ppfUsable[j].fillOpacity),
            paths: showWallBlindspots ? computeIntersection(newPathArr, clippedView, map) : newPathArr
          }))
        }

        const furthestPaths = calculateBoundPoints({ originAngle: horizontalAngle, distance, bound: furtherBound, closer: false, ...args }) // calculate path from final analytic 10 PPF --> marker border
        const outerPath = [...pathArr[pathArr.length - 1].path, ...furthestPaths]
        polygonArray.push(new google.maps.Polygon({
          ...defaults,
          fillColor: ppfZoneColorOverride ? (useBlindspot && ppfUsable[pathArr.length - 2] ? ppfUsable[pathArr.length - 2].color : ppfUsable[pathArr.length - 1].color) : color,
          fillOpacity: useBlindspot && ppfUsable[pathArr.length - 2] ? ppfUsable[pathArr.length - 2].fillOpacity : ppfUsable[pathArr.length - 1].fillOpacity,
          paths: showWallBlindspots ? computeIntersection(outerPath, clippedView, map) : outerPath
        }))
      } else {
        const onePath = this.getPaths(calculateVertex({ angle: horizontalAngle, distance, heading, cameraMarkerPosition })) // if no analytic zones visible, generate one large zone
        polygonArray.push(new google.maps.Polygon({
          ...defaults,
          fillColor: ppfZoneColorOverride ? ppfRaw[0].color : color,
          fillOpacity: ppfRaw[0].fillOpacity,
          paths: showWallBlindspots ? computeIntersection(onePath, clippedView, map) : onePath
        }))
      }

      this.view = polygonArray
    } else if (horizontalAngle < 360) {
      for (let j = 0; j < ppfRaw.length; j++) {
        const calculatedDistance = (resolution * 180) / (ppfRaw[j].value * horizontalAngle * Math.PI)

        if (calculatedDistance < distance) {
          pathArr.push(this.getPaths(calculateVertex({ angle: horizontalAngle, distance: calculatedDistance, heading, cameraMarkerPosition })))
        } else break
      }

      if (pathArr.length > 0) {
        for (let j = 0; j + 1 < pathArr.length; j++) { // slice part of full circle depending on HAoV value
          const path = j === 0 ? pathArr[j] : pathArr[j].slice(0, Math.ceil(horizontalAngle + 1)).concat(pathArr[j + 1].slice(0, Math.ceil(horizontalAngle + 1)).reverse())
          circleArray.push(new google.maps.Polygon({
            ...defaults,
            fillColor: ppfZoneColorOverride ? ppfRaw[j].color : color,
            fillOpacity: ppfRaw[j].fillOpacity,
            paths: showWallBlindspots ? computeIntersection(path, clippedCircle, map) : path
          }))
        }

        const tempPath = this.getPaths(calculateVertex({ angle: horizontalAngle, distance, heading, cameraMarkerPosition }))
        const newPathArr = tempPath.concat(pathArr[pathArr.length - 1].reverse())
        circleArray.push(new google.maps.Polygon({
          ...defaults,
          fillColor: ppfZoneColorOverride ? ppfRaw[circleArray.length].color : color,
          fillOpacity: ppfRaw[circleArray.length].fillOpacity,
          paths: showWallBlindspots ? computeIntersection(newPathArr, clippedCircle, map) : newPathArr
        }))
      } else {
        const tempPath = this.getPaths(calculateVertex({ angle: horizontalAngle, distance, heading, cameraMarkerPosition }))
        circleArray.push(new google.maps.Polygon({
          ...defaults,
          fillColor: ppfZoneColorOverride ? ppfRaw[circleArray.length].color : color,
          fillOpacity: ppfRaw[circleArray.length].fillOpacity,
          paths: showWallBlindspots ? computeIntersection(tempPath, clippedCircle, map) : tempPath
        }))
      }

      this.view = circleArray
    } else if (horizontalAngle === 360) {
      for (let j = 0; j < ppfRaw.length; j++) {
        const calculatedDistance = (resolution * 180) / (ppfRaw[j].value * horizontalAngle * Math.PI)

        if (calculatedDistance < distance) {
          const tempPath = calculateVertex({ angle: horizontalAngle, distance: calculatedDistance, heading, cameraMarkerPosition, showAnalyticZones })
          pathArr.push({ path: tempPath, distance: calculatedDistance })
        } else break
      }

      if (pathArr.length > 0) {
        circleArray.push(new google.maps.Polygon({ // use circle path for first zone
          ...defaults,
          fillColor: ppfZoneColorOverride ? ppfRaw[0].color : color,
          fillOpacity: ppfRaw[0].fillOpacity,
          paths: showWallBlindspots ? computeIntersection(pathArr[0].path, clippedCircle, map) : pathArr[0].path
        }))

        for (let j = 1; j + 1 < pathArr.length; j++) { // generate subsequent zones as rings
          const newPathArr = [pathArr[j + 1].path, pathArr[j].path.slice().reverse()]
          circleArray.push(new google.maps.Polygon({
            ...defaults,
            fillColor: ppfZoneColorOverride ? ppfRaw[j].color : color,
            fillOpacity: ppfRaw[j].fillOpacity,
            paths: showWallBlindspots ? computeRingIntersection(newPathArr, clippedCircle, map) : newPathArr
          }))
        }

        // generate final zone from edge of previous PPF region
        const tempPath = calculateVertex({ angle: horizontalAngle, distance, heading, cameraMarkerPosition, showAnalyticZones })
        const newPathArr = [tempPath, pathArr[pathArr.length - 1].path.slice().reverse()]
        circleArray.push(new google.maps.Polygon({
          ...defaults,
          fillColor: ppfZoneColorOverride ? ppfRaw[circleArray.length].color : color,
          fillOpacity: ppfRaw[circleArray.length].fillOpacity,
          paths: showWallBlindspots ? computeRingIntersection(newPathArr, clippedCircle, map) : newPathArr
        }))
      } else {
        const tempPath = this.getPaths(calculateVertex({ angle: horizontalAngle, distance, heading, cameraMarkerPosition }))
        circleArray.push(new google.maps.Polygon({ // use circle path for first zone
          ...defaults,
          fillColor: ppfZoneColorOverride ? ppfRaw[0].color : color,
          fillOpacity: ppfRaw[0].fillOpacity,
          paths: showWallBlindspots ? computeIntersection(tempPath, clippedCircle, map) : tempPath
        }))
      }

      this.view = circleArray
    }

    addToMarkers('analyticZones', this.view) // add analytic zone polygons to markers list
    this.updateView(el => el.addListener('click', setCurrentIfApplicable))
  }

  diffProps(nextProps) {
    const keysToCompare = ['tilt', 'horizontalAngleOfView', 'verticalAngleOfView', 'distance', 'heading', 'subjectMarkerDragging',
      'angleMarkerDragging', 'cameraDragging', 'draggingOverride', 'showAnalyticZones', 'showCameraBlindspot', 'ppf', 'color',
      'fovMode', 'showCameraComponents', 'showWallBlindspots', 'walls', 'mapLoaded', 'ppfZoneBorders', 'ppfZoneColorOverride', 'ppfZoneColors',
      'cameraMarkerPosition']

    return keysToCompare.some(key => {
      return nextProps[key] !== this.props[key]
    })
  }

  componentWillReceiveProps(nextProps) {
    const { subjectMarkerDragging, angleMarkerDragging, cameraDragging, isLens, draggingOverride,
      showAnalyticZones, activeInUI, selectionOverride, showCameraComponents } = nextProps

    // if anything is dragging and any dragging state has changed
    if (subjectMarkerDragging ||
      angleMarkerDragging ||
      cameraDragging ||
      (isLens && draggingOverride) ||
      !showAnalyticZones ||
      !showCameraComponents) {
        if (this.view) {
          this.updateView(el => el.setMap(null))
          this.view = null
        }
    } else if ((!subjectMarkerDragging &&
      !angleMarkerDragging &&
      !cameraDragging &&
      (!isLens || !draggingOverride)) &&
      showAnalyticZones &&
      this.diffProps(nextProps)) {
        this.drawView(nextProps)
    }

    if (this.view && (activeInUI !== this.props.activeInUI || selectionOverride !== this.props.selectionOverride)) {
      this.updateView(el => el.setOptions({ zIndex: activeInUI || selectionOverride ? 104 : 102 }))
    }
  }

  shouldComponentUpdate() {
    return false
  }

  render() {
    return null
  }
}



// WEBPACK FOOTER //
// ./src/components/Camera/CameraView/AnalyticZones.js